import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * 
 * @param {Object|null} matchdatas - Match data containing game information
 * @param {Object} config - Configuration object with wssLink and Topic_Match_Odds
 * @returns {Object} { matchOdd, isConnected, reconnectAttempts }
 */
export const useMatchOddsWebSocket = (matchdatas, config) => {
  const [matchOdd, setMatchOdd] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const [marketIds, setMarketIds] = useState([]);
  const marketIdsRef = useRef([]);

  const wsRef = useRef(null);
  const subscribeChannels = useRef([]);
  const heartbeatInterval = useRef(null);
  const reconnectTimeout = useRef(null);
  const mountedRef = useRef(true);
  const isReconnecting = useRef(false);
  const isConnecting = useRef(false);
  const reconnectAttemptsRef = useRef(0);

  // Compute marketIds when matchdatas changes (runs only when matchdatas changes)
  useEffect(() => {
    console.debug('[WS] matchdatas changed:', matchdatas);
    try {
      if (!matchdatas?.game) {
        console.debug('[WS] No matchdatas.game — clearing marketIds');
        setMarketIds([]);
        marketIdsRef.current = [];
        return;
      }

      const ids = matchdatas.game.flatMap((game) =>
        (game.matchData || []).flatMap((match) =>
          (match.matchodd || [])
            .filter((odd) => odd.marketName === "Match Odds")
            .map((odd) => String(odd.marketId))
        )
      );

      const uniqueIds = Array.from(new Set(ids));
      console.log('[WS] Computed marketIds:', uniqueIds);
      setMarketIds(uniqueIds);
      marketIdsRef.current = uniqueIds;
    } catch (err) {
      console.error('[WS] Error computing marketIds from matchdatas:', err);
      setMarketIds([]);
      marketIdsRef.current = [];
    }
  }, [matchdatas]);

  // Cleanup WebSocket safely
  const cleanupWebSocket = useCallback(() => {
    console.log('[WS] cleanupWebSocket invoked');
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
      console.debug('[WS] Heartbeat interval cleared');
    }

    if (wsRef.current) {
      try {
        console.debug('[WS] Closing WebSocket instance');
        wsRef.current.close();
      } catch (e) {
        console.error('[WS] Error closing WebSocket:', e);
      }
      wsRef.current = null;
    }

    subscribeChannels.current = [];
    console.debug('[WS] subscribeChannels reset');

    if (mountedRef.current) {
      setIsConnected(false);
      console.debug('[WS] isConnected set to false (cleanup)');
    }
  }, []);

  // Handle reconnection with exponential backoff
  const reconnectWebSocket = useCallback(() => {
    if (!mountedRef.current) {
      console.debug('[WS] reconnect skipped: component unmounted');
      return;
    }
    if (isReconnecting.current) {
      console.debug('[WS] reconnect skipped: already reconnecting');
      return;
    }

    isReconnecting.current = true;
    console.warn('[WS] 🔄 Reconnecting WebSocket...');

    clearTimeout(reconnectTimeout.current);
    cleanupWebSocket();

    const backoffTime = Math.min(3000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
    console.log(`[WS] Reconnect backoffTime = ${backoffTime}ms (attempt ${reconnectAttemptsRef.current})`);

    reconnectTimeout.current = setTimeout(() => {
      if (!mountedRef.current) {
        console.debug('[WS] reconnect aborted: component unmounted during timeout');
        isReconnecting.current = false;
        return;
      }
      reconnectAttemptsRef.current += 1;
      setReconnectAttempts(reconnectAttemptsRef.current);
      console.log('[WS] Reconnect attempt #: ', reconnectAttemptsRef.current);
      isReconnecting.current = false;
      startWebSocket();
    }, backoffTime);
  }, [cleanupWebSocket]);

  // Forward declarations
  let handleOpen, handleMessage, handleClose, handleError, startWebSocket;

  // Main WebSocket initialization
  startWebSocket = useCallback(() => {
    console.debug('[WS] startWebSocket called');
    if (!config?.wssLink) {
      console.error('[WS] startWebSocket aborted: no config.wssLink');
      return;
    }
    if (isConnecting.current) {
      console.debug('[WS] startWebSocket aborted: already connecting');
      return;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.debug('[WS] startWebSocket aborted: socket already open');
      return;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
      console.debug('[WS] startWebSocket aborted: socket connecting');
      return;
    }
    if (!mountedRef.current) {
      console.debug('[WS] startWebSocket aborted: component unmounted');
      return;
    }

    try {
      isConnecting.current = true;
      console.log('[WS] Creating new WebSocket ->', config.wssLink);
      const ws = new WebSocket(config.wssLink);
      wsRef.current = ws;

      ws.addEventListener('open', handleOpen);
      ws.addEventListener('message', handleMessage);
      ws.addEventListener('close', handleClose);
      ws.addEventListener('error', handleError);
    } catch (error) {
      console.error('[WS] WebSocket initialization error:', error);
      isConnecting.current = false;
      reconnectWebSocket();
    }
  }, [config?.wssLink, reconnectWebSocket]);

  // WebSocket Open Handler
  handleOpen = useCallback(() => {
    if (!mountedRef.current) {
      console.debug('[WS] handleOpen called but component not mounted');
      return;
    }

    isConnecting.current = false;
    console.log('[WS] ✅ WebSocket connected');
    setIsConnected(true);

    reconnectAttemptsRef.current = 0;
    setReconnectAttempts(0);

    const ids = marketIdsRef.current || [];
    console.debug('[WS] handleOpen marketIdsRef.current =', ids);

    if (!ids.length) {
      console.warn('[WS] ⚠️ No market IDs to subscribe to on open — waiting for data');
      // still keep connected for when marketIds arrive
    } else {
      ids.forEach((marketId) => {
        const topic = `${config.Topic_Match_Odds}${marketId}`;
        if (!subscribeChannels.current.includes(topic)) {
          try {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ action: 'subscribe', topic }));
              subscribeChannels.current.push(topic);
              console.log(`[WS] 📡 Subscribed to: ${topic}`);
            }
          } catch (error) {
            console.error(`[WS] Failed to subscribe to ${topic}:`, error);
          }
        } else {
          console.debug(`[WS] Already subscribed to: ${topic}`);
        }
      });
    }

    // Heartbeat: Send ping every 10 seconds
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
      console.debug('[WS] Cleared existing heartbeat interval before starting new one');
    }

    heartbeatInterval.current = setInterval(() => {
      if (!mountedRef.current) return;

      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ action: 'ping' }));
          console.debug('[WS] Sent heartbeat ping');
        } catch (error) {
          console.error('[WS] Heartbeat ping failed:', error);
        }
      } else if (wsRef.current?.readyState === WebSocket.CLOSED) {
        clearInterval(heartbeatInterval.current);
        heartbeatInterval.current = null;
        console.warn('[WS] Heartbeat stopped — socket closed, scheduling reconnect');
        reconnectWebSocket();
      }
    }, 10000);
  }, [config?.Topic_Match_Odds, reconnectWebSocket]);

  // WebSocket Message Handler
  handleMessage = useCallback((event) => {
    if (!mountedRef.current) {
      // console.debug('[WS] handleMessage called but component not mounted');
      return;
    }

    // console.debug('[WS] Raw message received:', event.data);
    try {
      const payload = JSON.parse(event.data);
      const { topic, marketData } = payload;

      if (topic && topic.startsWith(config.Topic_Match_Odds)) {
        try {
          const parsedData = JSON.parse(marketData);
          const marketId = topic.split('/').pop();
          // console.log(`[WS] Received market update for ${marketId}`);

          setMatchOdd((prev) => {
            const next = { ...prev, [marketId]: parsedData };
            // console.debug('[WS] Updated matchOdd:', next);
            return next;
          });
        } catch (parseError) {
          console.error('[WS] Invalid JSON in marketData:', parseError, 'raw marketData:', marketData);
        }
      } else if (payload && payload.action === 'pong') {
        console.debug('[WS] Received app-level pong:', payload);
      } else if (payload && payload.action === 'ping') {
        // server ping - reply with pong
        try {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ action: 'pong'}));
            console.debug('[WS] Replied with pong to server ping');
          }
        } catch (err) {
          console.error('[WS] Failed to reply pong:', err);
        }
      } else {
        console.debug('[WS] Non-market message received:', payload);
      }
    } catch (error) {
      console.error('[WS] Error parsing WebSocket message:', error, 'raw:', event.data);
    }
  }, [config?.Topic_Match_Odds]);

  // WebSocket Close Handler
  handleClose = useCallback((ev) => {
    if (!mountedRef.current) {
      console.debug('[WS] handleClose called but component not mounted');
      return;
    }

    isConnecting.current = false;
    setIsConnected(false);
    console.warn('[WS] ⚠️ WebSocket connection closed:', ev);

    reconnectWebSocket();
  }, [reconnectWebSocket]);

  // WebSocket Error Handler
  handleError = useCallback((error) => {
    if (!mountedRef.current) {
      console.debug('[WS] handleError called but component not mounted');
      return;
    }

    isConnecting.current = false;
    setIsConnected(false);
    console.error('[WS] ❌ WebSocket error:', error);

    cleanupWebSocket();
    reconnectWebSocket();
  }, [cleanupWebSocket, reconnectWebSocket]);

  // Effect: Handle tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!mountedRef.current) return;

      console.debug('[WS] visibilitychange - hidden?', document.hidden);
      if (!document.hidden) {
        console.log('[WS] Tab visible - checking connection state');
        if (!isConnecting.current && wsRef.current?.readyState !== WebSocket.OPEN) {
          console.log('[WS] Tab visible - starting socket (if needed)');
          startWebSocket();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [startWebSocket]);

  // Effect: Initialize WebSocket and cleanup (only once on mount)
  useEffect(() => {
    console.log('[WS] Hook mounted; config.wssLink =', config?.wssLink);
    if (!config?.wssLink) {
      console.error('[WS] WebSocket URL not configured');
      return;
    }

    mountedRef.current = true;

    if (marketIdsRef.current.length > 0) {
      console.log('[WS] marketIds exist on mount -> starting socket');
      startWebSocket();
    } else {
      console.log('[WS] No marketIds on mount -> socket will start when data arrives');
    }

    return () => {
      console.log('[WS] Hook unmounting - cleaning up');
      mountedRef.current = false;
      isConnecting.current = false;

      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
        heartbeatInterval.current = null;
        console.debug('[WS] Cleared heartbeat on unmount');
      }

      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
        console.debug('[WS] Cleared reconnect timeout on unmount');
      }

      if (wsRef.current) {
        wsRef.current.removeEventListener('open', handleOpen);
        wsRef.current.removeEventListener('message', handleMessage);
        wsRef.current.removeEventListener('close', handleClose);
        wsRef.current.removeEventListener('error', handleError);

        try {
          wsRef.current.close();
          console.debug('[WS] Closed wsRef on unmount');
        } catch (e) {
          console.error('[WS] Error closing wsRef on unmount:', e);
        }
        wsRef.current = null;
      }

      subscribeChannels.current = [];
      console.debug('[WS] subscribeChannels cleared on unmount');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.wssLink]);

  // Effect: when marketIds change, (re)subscribe/unsubscribe as needed and start socket if missing
  useEffect(() => {
    console.debug('[WS] marketIds effect triggered:', marketIds);
    if (!mountedRef.current) return;

    marketIdsRef.current = marketIds || [];

    if (!marketIdsRef.current.length) {
      console.log('[WS] marketIds empty -> unsubscribing all if connected');
      if (wsRef.current?.readyState === WebSocket.OPEN && subscribeChannels.current.length) {
        subscribeChannels.current.forEach((topic) => {
          try {
            wsRef.current.send(JSON.stringify({ action: 'unsubscribe', topic }));
            console.log(`[WS] 📵 Unsubscribed from: ${topic}`);
          } catch (e) {
            console.error(`[WS] Failed to unsubscribe from ${topic}:`, e);
          }
        });
        subscribeChannels.current = [];
      }
      return;
    }

    if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
      console.log('[WS] Socket missing/closed -> starting socket to subscribe to new marketIds');
      startWebSocket();
      return;
    }

    if (wsRef.current.readyState === WebSocket.OPEN) {
      const newTopics = marketIdsRef.current.map((id) => `${config.Topic_Match_Odds}${id}`);
      console.debug('[WS] Reconciling subscriptions. newTopics:', newTopics, 'current:', subscribeChannels.current);

      // Unsubscribe topics that are no longer needed
      subscribeChannels.current.forEach((topic) => {
        if (!newTopics.includes(topic)) {
          try {
            wsRef.current.send(JSON.stringify({ action: 'unsubscribe', topic }));
            console.log(`[WS] 📵 Unsubscribed from: ${topic}`);
          } catch (error) {
            console.error(`[WS] Failed to unsubscribe from ${topic}:`, error);
          }
        }
      });

      // Subscribe to new topics
      newTopics.forEach((topic) => {
        if (!subscribeChannels.current.includes(topic)) {
          try {
            wsRef.current.send(JSON.stringify({ action: 'subscribe', topic }));
            subscribeChannels.current.push(topic);
            console.log(`[WS] 📡 Subscribed to: ${topic}`);
          } catch (error) {
            console.error(`[WS] Failed to subscribe to ${topic}:`, error);
          }
        } else {
          console.debug(`[WS] Already subscribed to: ${topic}`);
        }
      });

      subscribeChannels.current = newTopics.slice();
      console.debug('[WS] Subscriptions reconciled ->', subscribeChannels.current);
    }
  }, [marketIds, config?.Topic_Match_Odds, startWebSocket]);

  return {
    matchOdd,
    isConnected,
    reconnectAttempts,
  };
};
